function topLevelCopy(object) {
    const entries = Object.entries(object);
    const copiedObject = {};
    for (const [key, value] of entries) {
        if (typeof value !== 'object') {
            copiedObject[key] = value;
        }
    }
    return copiedObject;
}

export function packageData(data, platform, lang, showSimpleInstructions) {
    if (!data) return null;
    
    const platformKey = data[platform] ? platform : data.generic ? 'generic' : 'ios'
    let content = data[platformKey][lang] ? {...data[platformKey][lang]} : {...data[platformKey].en}

    // a simple inelegant hack to get an alternative, simplified version of the instructions
    if (showSimpleInstructions) {
        if (Object.keys(content).includes('simple_summary_no_video_html')) {
          content.summary_no_video_html = content.simple_summary_no_video_html;
        }

        if (Object.keys(content).includes('simple_steps')) {
          content.steps = content.simple_steps;
        }
    }

    return {
        ...topLevelCopy(data),
        ...topLevelCopy(data[platformKey]),
        ...content,
    }
} 

const getGenericUploadInstructions = (dataType, description, logo, serviceName, part1InstructionsLink) => {
    const content = {
      en: {
        simple_summary_no_video_html: '',
        simple_steps: [
          {
            step_type: 'numbered_step',
            number: '1',
            description_html: '<p>To upload your data, simply click <b>"Choose File"</b> below; find the ZIP file you saved and upload it.</p>'
          },
          {
            step_type: 'numbered_step',
            number: '2',
            description_html: '<p>Since some files are large, we recommend being connected to Wi-Fi so that the upload process is as fast as possible.</p>'
          },
          {
            step_type: 'emoji_step',
            emoji: '💡',
            description_html: '<p>PLEASE NOTE THAT WE USE A SECURE UPLOADING SERVICE TO PROTECT YOUR DATA AND PRIVACY.</p>'
          }
        ],
        summary_no_video_html: '<p>Please upload the data file by following the instructions below:</p>',
        steps: [
          {
            step_type: 'numbered_step',
            number: '1',
            description_html: '<p>You should have been notified by email that your data download has been sent to you - this will have been an email sent to you within one or two days after you requested it.</p>'
          },
          {
            step_type: 'numbered_step',
            number: '2',
            description_html: '<p>In order to complete part two you will need to have saved the data file.</p>'
          },
          {
            step_type: 'numbered_step',
            number: '3',
            description_html: `<p>To save the download file, open the email sent to you by ${serviceName}.</p>`
          },
          {
            step_type: 'numbered_step',
            number: '4',
            description_html: `<p>Follow the instructions — e.g. if it asks for your password to access the file.</p>`
          },
          {
            step_type: 'numbered_step',
            number: '5',
            description_html: `<p>Download the file to your mobile phone or computer.</p>`
          },
          {
            step_type: 'numbered_step',
            number: '6',
            description_html: '<p>To upload your data, simply tap on <b>Choose File below</b> — find the file that you just saved and then upload it. As some files are large we suggest that you are connecting to wifi to make the upload process as quick as possible.</p>'
          },
          {
            step_type: 'emoji_step',
            emoji: '💡',
            description_html: `<p>PLEASE NOTE WE USE A SECURE UPLOAD SERVICE TO PROTECT YOUR DATA AND YOUR PRIVACY.</p>`
          },
        ],
        part_1_instructions_link: part1InstructionsLink,
      },
      pt: {
        simple_summary_no_video_html: '',
        simple_steps: [
          {
            step_type: 'numbered_step',
            number: '1',
            description_html: '<p>Para fazer o upload dos seus dados, simplesmente clique em <b>"Escolher Arquivo"</b> em baixo — encontre o ficheiro ZIP que acabou de guardar e faça o upload do mesmo.</p>'
          },
          {
            step_type: 'numbered_step',
            number: '2',
            description_html: '<p>Como alguns ficheiros são grandes, sugerimos que esteja conectado ao Wi-Fi para tornar o processo de upload o mais rápido possível.</p>'
          },
          {
            step_type: 'emoji_step',
            emoji: '💡',
            description_html: '<p>POR FAVOR, NOTE QUE USAMOS UM SERVIÇO DE UPLOAD SEGURO PARA PROTEGER SEUS DADOS E A SUA PRIVACIDADE.</p>'
          }
        ],
        summary_no_video_html: '<p>Carregue o arquivo de dados seguindo as instruções abaixo:</p>',
        steps: [
          {
            step_type: 'numbered_step',
            number: '1',
            description_html: '<p>Você deveria ter sido notificado por e-mail de que seu download de dados foi enviado a você - este será um e-mail enviado a você dentro de um ou dois dias após você solicitá-lo.</p>'
          },
          {
            step_type: 'numbered_step',
            number: '2',
            description_html: '<p>Para concluir a parte dois, você precisará salvar o arquivo de dados.</p>'
          },
          {
            step_type: 'numbered_step',
            number: '3',
            description_html: `<p>Para salvar o arquivo de download, abra o e-mail enviado a você por ${serviceName}</p>`
          },
          {
            step_type: 'numbered_step',
            number: '4',
            description_html: `<p>Siga as instruções - ex: podem pedir o seu login para aceder ao ficheiro.</p>`
          },
          {
            step_type: 'numbered_step',
            number: '5',
            description_html: '<p>Baixe o arquivo para o seu celular ou computador.</p>'
          },
          {
            step_type: 'numbered_step',
            number: '6',
            description_html: '<p>Agora basta selecionar upload abaixo - encontre o arquivo que você acabou de salvar e faça o upload para nós. Como alguns arquivos são grandes, sugerimos que você esteja conectado a uma rede Wi-Fi para tornar o processo de upload o mais rápido possível.</p>'
          },
          {
            step_type: 'emoji_step',
            emoji: '💡',
            description_html: `<p>USAMOS UM SERVIÇO UPLOAD SEGURO PARA PROTEGER SEUS DADOS E PRIVACIDADE.</p>`
          },
        ],
      },
      es: {
        simple_summary_no_video_html: '',
        simple_steps: [
          {
            step_type: 'numbered_step',
            number: '1',
            description_html: '<p>Para subir tus datos, simplemente haz clic en <b>"Elegir archivo"</b> abajo; encuentra el archivo ZIP que acabas de guardar y súbelo.</p>'
          },
          {
            step_type: 'numbered_step',
            number: '2',
            description_html: '<p>Dado que algunos archivos son grandes, te recomendamos estar conectado a Wi-Fi para que el proceso de subida sea lo más rápido posible.</p>'
          },
          {
            step_type: 'emoji_step',
            emoji: '💡',
            description_html: '<p>POR FAVOR, TEN EN CUENTA QUE UTILIZAMOS UN SERVICIO DE SUBIDA SEGURO PARA PROTEGER TUS DATOS Y TU PRIVACIDAD.</p>'
          }
        ],
        steps: [
          {
            step_type: 'numbered_step',
            number: '1',
            description_html: '<p>Deberías haber sido notificado por correo electrónico, que se te enviaron la descarga de datos; debería haber llegado una mensaje de correo electrónico dentro de uno o dos días desde que hiciste el pedido.</p>'
          },
          {
            step_type: 'numbered_step',
            number: '2',
            description_html: '<p>Para completar la segunda parte, debes copiar el archivo de datos.</p>'
          },
          {
            step_type: 'numbered_step',
            number: '3',
            description_html: `<p>Para guardar el archivo de descarga, abre el correo electrónico que te envió ${serviceName}</p>`
          },
          {
            step_type: 'numbered_step',
            number: '4',
            description_html: `<p>Siga las instrucciones - ex: si te piden que hagas login para descargar el archivo.</p>`
          },
          {
            step_type: 'numbered_step',
            number: '5',
            description_html: '<p>Descargue el archivo a su teléfono móvil o computadora.</p>'
          },
          {
            step_type: 'numbered_step',
            number: '6',
            description_html: '<p>Ahora simplemente selecciona “cargar” (boton abajo): busque el archivo que acabaste de guardar, y luego cárgalo. Como algunos archivos son grandes, te sugerimos que te conectes a wifi para que el proceso de carga sea lo más rápido posible.</p>'
          },
          {
            step_type: 'emoji_step',
            emoji: '💡',
            description_html: `<p>POR FAVOR NOTAR QUE UTILIZAMOS UN SERVICIO DE CARGA SEGURO PARA PROTEGER TUS DATOS Y PRIVACIDAD.</p>`
          },
        ],
      },
      id: {
        summary_no_video_html: '<p>Silakan unggah file data dengan mengikuti petunjuk di bawah ini:</p>',
        steps: [
          {
            step_type: 'numbered_step',
            number: '1',
            description_html: '<p>Jika Anda perlu diberi tahu oleh email elektronik bahwa Anda melihat lingkungan pengunduhan data; jika Anda mendengar surat elektronik di satu atau dua hari setelah Anda memintanya.</p>'
          },
          {
            step_type: 'numbered_step',
            number: '2',
            description_html: '<p>Untuk melengkapi bagian kedua, Anda harus menjaga arsip data.</p>'
          },
          {
            step_type: 'numbered_step',
            number: '3',
            description_html: `<p>Untuk menjaga file unduhan; Buka email yang dikirimkan kepada Anda oleh ${serviceName}</p>`
          },
          {
            step_type: 'numbered_step',
            number: '4',
            description_html: `<p>Ikuti instruksi - ex: jika meminta kata sandi Anda untuk mengakses file.</p>`
          },
          {
            step_type: 'numbered_step',
            number: '5',
            description_html: '<p>Unduh file ke ponsel atau komputer Anda.</p>'
          },
          {
            step_type: 'numbered_step',
            number: '6',
            description_html: '<p>Sekarang cukup pilih isi lanjutan: buka file yang akan disimpan dan diluego cárguelo. Karena beberapa file berukuran besar, kami sarankan Anda menyambungkan ke wifi untuk mempercepat proses pengunggahan.</p>'
          },
          {
            step_type: 'emoji_step',
            emoji: '💡',
            description_html: `<p>HARAP DIPERHATIKAN KAMI MENGGUNAKAN LAYANAN UPLOAD AMAN UNTUK MELINDUNGI DATA DAN PRIVASI ANDA.</p>`
          },
        ],
      },
    }
    return {
      data_type: dataType,
      data_type_description: description,
      logo,
      capture_type: 'file',
      use_in_app_screen_recorder: false,
      require_consent: true,
      generic: {
        ...content,
      }
    }
};


const getGenericAppleUploadInstructions = (dataType, description, logo, serviceName, part1InstructionsLink) => {
    const content = {
        en: {
            simple_summary_no_video_html: '',
            simple_steps: [
                {
                    step_type: 'numbered_step',
                    number: '1',
                    description_html: '<p>To upload your data, simply click <b>"Choose File"</b> below; find the ZIP file you saved and upload it.</p>'
                },
                {
                    step_type: 'numbered_step',
                    number: '2',
                    description_html: '<p>Since some files are large, we recommend being connected to Wi-Fi so that the upload process is as fast as possible.</p>'
                },
                {
                    step_type: 'emoji_step',
                    emoji: '💡',
                    description_html: '<p>PLEASE NOTE THAT WE USE A SECURE UPLOADING SERVICE TO PROTECT YOUR DATA AND PRIVACY.</p>'
                }
            ],
            summary_no_video_html: '<p>Please upload the data file by following the instructions below:</p>',
            steps: [
                {
                    step_type: 'numbered_step',
                    number: '1',
                    description_html: '<p>You should have been notified by email that your data download has been sent to you - this will have been an email sent to you within four to six days after you requested it.</p>'
                },
                {
                    step_type: 'numbered_step',
                    number: '2',
                    description_html: '<p>In order to complete part two you will need to have saved the data file.</p>'
                },
                {
                    step_type: 'numbered_step',
                    number: '3',
                    description_html: `<p>To save the download file, open the email from Apple saying 'Your download request is complete'.</p>`
                },
                {
                    step_type: 'numbered_step',
                    number: '4',
                    description_html: `<p>In the email, click on 'Get your data'.</p>`
                },
                {
                    step_type: 'numbered_step',
                    number: '5',
                    description_html: `<p>Log into your account.</p>`
                },
                {
                    step_type: 'numbered_step',
                    number: '6',
                    description_html: `<p>Click on maps and download your data.</p>`
                },
                {
                    step_type: 'numbered_step',
                    number: '7',
                    description_html: `<p>Download the file to your mobile phone or computer.</p>`
                },
                {
                    step_type: 'numbered_step',
                    number: '8',
                    description_html: '<p>To upload your data, simply tap on <b>Choose File below</b> — find the file that you just saved and then upload it. As some files are large we suggest that you are connecting to wifi to make the upload process as quick as possible.</p>'
                },
                {
                    step_type: 'emoji_step',
                    emoji: '💡',
                    description_html: `<p>PLEASE NOTE WE USE A SECURE UPLOAD SERVICE TO PROTECT YOUR DATA AND YOUR PRIVACY.</p>`
                },
            ],
            part_1_instructions_link: part1InstructionsLink,
        },
    }
    return {
        data_type: dataType,
        data_type_description: description,
        logo,
        capture_type: 'file',
        use_in_app_screen_recorder: false,
        require_consent: true,
        generic: {
            ...content,
        }
    }
};



const content = {
    'app_store_purchase_history': {
        ios: {
            en: {
                video_thumbnail: 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/app-store-purchase-history-ios/ios-app-store-purchase-video-thumbnail.jpeg',
                // video_url: 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/app-store-purchase-history-ios/in-app-purchase-video.mp4',
                // video_poster: 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/app-store-purchase-history-ios/app-store-purchase-poster.jpg',
                summary_html: "<p>Retro jobs are simple, quick and a bit of fun too. Read the steps below, and then you're ready to hit record and scroll through your App Store purchase history.</p>",
                summary_no_video_html: "<p>Retro jobs are simple, quick and a bit of fun too. Read the steps below, and then you're ready to hit record and scroll through your App Store purchase history.</p>",
                steps: [
                    {
                        step_type: 'numbered_step',
                        number: '1',
                        description_html: '<p>Open the <b>App Store</b> app.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '2',
                        description_html: "<p>In the app, tap on your account (via your profile icon, top-right). <i>Do not</i> tap on Purchases, it's the wrong section for this task.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '3',
                        description_html: '<p>Instead tap on your account name (usually the first item). Scroll down and tap on <b>Purchase History</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '4',
                        description_html: `<p>Tap on <b>Showing:</b> to update the filtering.</p>`
                    },
                    {
                        step_type: 'numbered_step',
                        number: '5',
                        description_html: `<p>Change date range to <b>${new Date().getMonth() < 3 ? new Date().getFullYear() - 1 : new Date().getFullYear()}</b> and Cost to both <b>Free</b> and <b>Paid</b>.</p>`
                    },
                    {
                        step_type: 'numbered_step',
                        number: '6',
                        description_html: `<p>Tap <b>Apply</b> and begin screen recording.</p>`
                    },
                    {
                        step_type: 'numbered_step',
                        number: '7',
                        description_html: '<p>Scroll to the end before you stop recording.</p>'
                    }
                ],
            },
            es: {
                data_type_description: 'Historial de compras de la tienda de aplicaciones',
                summary_html: "<p>Los trabajos retro son simples, rápidos y también un poco divertidos. Mire el video y lea los pasos a continuación, y luego estará listo para presionar grabar y desplazarse por el historial de compras de su App Store.</p>",
                summary_no_video_html: "<p>Los trabajos retro son simples, rápidos y también un poco divertidos. Lea los pasos a continuación, y luego estará listo para presionar grabar y desplazarse por el historial de compras de su App Store.</p>",
                steps: [
                    {
                    step_type: 'numbered_step',
                    number: '1',
                    description_html: '<p>Abra la aplicación <b>App Store</b>.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '2',
                    description_html: "<p>En la aplicación, toque su cuenta (a través del ícono de su perfil, arriba a la derecha). <i>No</i> toque Compras, es la sección incorrecta para esta tarea.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '3',
                    description_html: '<p>En su lugar, toque el nombre de su cuenta (generalmente el primer elemento). Desplázate hacia abajo y toca <b>Historial de compras</b>.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '4',
                    description_html: `<p>Cambie el intervalo de fechas a <b>${new Date().getFullYear()}</b> y <b>Todo</b>. Comience su grabación.</p>`
                    },
                    {
                    step_type: 'numbered_step',
                    number: '5',
                    description_html: '<p>Desplácese hasta el final antes de detener la grabación.</p>'
                    }
                ],
            }, 
            it: {
                data_type_description: "Cronologia degli acquisti dell'App Store",
                summary_html: "<p>I lavori retrò sono semplici, veloci e anche un po' divertenti. Guarda il video e leggi i passaggi seguenti, quindi sei pronto per registrare e scorrere la cronologia degli acquisti dell'App Store.</p>",
                summary_no_video_html: "<p>I lavori retrò sono semplici, veloci e anche un po' divertenti. Leggi i passaggi seguenti, quindi sei pronto per registrare e scorrere la cronologia degli acquisti dell'App Store.</p>",
                steps: [
                    {
                    step_type: 'numbered_step',
                    number: '1',
                    description_html: "<p>Apri l'app <b>App Store</b>.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '2',
                    description_html: "<p>Nell'app, tocca il tuo account (tramite l'icona del tuo profilo, in alto a destra). <i>Non</i> toccare Acquisti, è la sezione sbagliata per questa attività.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '3',
                    description_html: '<p>Tocca invece il nome del tuo account (di solito il primo elemento). Scorri verso il basso e tocca <b>Cronologia acquisti</b>.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '4',
                    description_html: `<p>Cambia l'intervallo di date in <b>${new Date().getFullYear()}</b> e <b>Tutti</b>. Inizia la tua registrazione.</p>`
                    },
                    {
                    step_type: 'numbered_step',
                    number: '5',
                    description_html: '<p>Scorri fino alla fine prima di interrompere la registrazione.</p>'
                    }
                ],
            },
            fr: {
                summary_html: "<p>Les tâches rétro sont simples, rapides et un peu amusantes aussi. Regardez la vidéo et lisez les étapes ci-dessous, puis vous êtes prêt à cliquer sur l'enregistrement et à faire défiler l'historique de vos achats sur l'App Store.</p>",
                summary_no_video_html: "<p>Les tâches rétro sont simples, rapides et un peu amusantes aussi. Lisez les étapes ci-dessous et vous serez ensuite prêt à cliquer sur Enregistrer et à faire défiler votre historique d'achats sur l'App Store.</p>",
                steps: [
                    {
                    step_type: 'numbered_step',
                    number: '1',
                    description_html: "<p>Ouvrez l'application <b>App Store</b>.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '2',
                    description_html: "<p>Dans l'application, appuyez sur votre compte (via l'icône de votre profil, en haut à droite). <i>Ne pas</i> appuyer sur Achats, ce n'est pas la bonne section pour cette tâche.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '3',
                    description_html: '<p>Appuyez plutôt sur le nom de votre compte (généralement le premier élément). Faites défiler vers le bas et appuyez sur <b>Historique des achats</b>.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '4',
                    description_html: `<p>Modifiez la plage de dates en <b>${new Date().getFullYear()}></b> et <b>Tous</b>. Démarrez votre enregistrement.</p>`
                    },
                    {
                    step_type: 'numbered_step',
                    number: '5',
                    description_html: "<p>Faites défiler jusqu'à la fin avant d'arrêter l'enregistrement.</p>"
                    }
                ],
            },
            de: {
                summary_html: "<p>Retro-Jobs sind einfach, schnell und machen auch ein bisschen Spaß. Sehen Sie sich das Video an und lesen Sie die Schritte unten, dann können Sie auf „Aufzeichnen“ klicken und durch Ihre Kaufhistorie im App Store scrollen.</p>",
                summary_no_video_html: "<p>Retro-Jobs sind einfach, schnell und machen auch ein bisschen Spaß. Lesen Sie die folgenden Schritte, dann können Sie auf „Aufzeichnen“ klicken und durch Ihre Kaufhistorie im App Store scrollen.</p>",
                steps: [
                    {
                    step_type: 'numbered_step',
                    number: '1',
                    description_html: '<p>Öffnen Sie die <b>App Store</b> App.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '2',
                    description_html: "<p>Tippen Sie in der App auf Ihr Konto (über Ihr Profilsymbol oben rechts). <i>Tippen Sie nicht</i> auf „Käufe“, das ist der falsche Abschnitt für diese Aufgabe.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '3',
                    description_html: '<p>Tippen Sie stattdessen auf Ihren Kontonamen (normalerweise das erste Element). Scrollen Sie nach unten und tippen Sie auf <b>Kaufhistorie</b>.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '4',
                    description_html: `<p>Ändern Sie den Datumsbereich in <b>${new Date().getFullYear()}</b> und <b>Alle</b>.“ Starten Sie Ihre Aufnahme.</p>`
                    },
                    {
                    step_type: 'numbered_step',
                    number: '5',
                    description_html: '<p>Scrollen Sie bis zum Ende, bevor Sie die Aufnahme beenden.</p>'
                    }
                ],
            },
            pt: {
                summary_html: "<p>Trabalhos retrô são simples, rápidos e divertidos também. Assista ao vídeo e leia as etapas abaixo, e então você estará pronto para gravar e percorrer seu histórico de compras na App Store.</p>",
                summary_no_video_html: "<p>Trabalhos retrô são simples, rápidos e divertidos também. Leia as etapas abaixo e você estará pronto para gravar e percorrer seu histórico de compras na App Store.</p>",
                steps: [
                    {
                    step_type: 'numbered_step',
                    number: '1',
                    description_html: '<p>Abra o aplicativo <b>App Store</b>.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '2',
                    description_html: "<p>No aplicativo, toque na sua conta (através do ícone do seu perfil, canto superior direito). <i>Não</i> toque em Compras, é a seção errada para esta tarefa.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '3',
                    description_html: '<p>Em vez disso, toque no nome da sua conta (geralmente o primeiro item). Role para baixo e toque em <b>Histórico de compras</b>.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '4',
                    description_html: `<p>Altere o intervalo de datas para <b>${new Date().getFullYear()}</b> e <b>All</b>. Comece sua gravação.</p>`
                    },
                    {
                    step_type: 'numbered_step',
                    number: '5',
                    description_html: '<p>Role até o final antes de parar a gravação.</p>'
                    }
                ],
            },
        },
        android: {
            en: {
                summary_html: '',
                summary_no_video_html: `
                    <p>Retro jobs are simple, quick and a bit of fun too. Watch the video and read the
                        steps below, and then you're ready to hit record and scroll through your Play Store Purchase History.</p>
                    <div class="text-with-icon">
                    <div class="icon">⚠️</div>
                    <div class="text">
                        We’ll only pay MSR rewards for videos that fully match the steps below.
                    </div>
                    </div>`,
                steps: [
                    {
                    step_type: 'numbered_step',
                    number: '1',
                    description_html: '<p>Start recording and open the <b>Google Play Store</b> app.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '2',
                    description_html: "<p>Tap on the <b>Profile Icon</b> in the top right corner of the screen.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '3',
                    description_html: '<p>Select <b>Payments and subscriptions</b>.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '4',
                    description_html: '<p>Click on <b>Budget and history</b>.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '5',
                    description_html: '<p>Scroll to the bottom of the page as fast as you can (allow the page to load if there are more items).</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '6',
                    description_html: '<p>Stop screen recording when done.</p>'
                    },
                ],
            },
            es: {
                data_type_description: 'Historial de compras de la tienda de aplicaciones',
                summary_html: '',
                summary_no_video_html: `
                    <p>Los trabajos retro son simples, rápidos y también un poco divertidos. Mira el video y lee el
                    pasos a continuación, y luego estará listo para presionar grabar y desplazarse por su Historial de compras de Play Store.</p>
                <div class="text-with-icon">
                <div class="icon">⚠️</div>
                <div clase="text">
                    Solo pagaremos recompensas de MSR por videos que coincidan completamente con los pasos a continuación.
                </div>
                </div>`,
                steps: [
                    {
                    step_type: 'numbered_step',
                    number: '1',
                    description_html: '<p>Antes de comenzar a grabar la pantalla, abra la aplicación <b>Google Play Store</b>.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '2',
                    description_html: "<p>Toca el ícono de menú en la esquina superior izquierda y selecciona <b>Cuenta</b>.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '3',
                    description_html: '<p>En la parte superior de la página, seleccione <b>Historial de compras</b>.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '4',
                    description_html: '<p>Ahora puede iniciar la grabación de pantalla.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '5',
                    description_html: '<p>Desplácese hacia abajo por la lista de compras de Play Store hasta llegar al final.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '6',
                    description_html: '<p>Asegúrese de capturar todo su historial de compras, luego detenga la grabación de video.</p>'
                    },
                ],
            },
            it: {
                data_type_description: "Cronologia degli acquisti dell'App Store",
                summary_html: '',
                summary_no_video_html: `
                    <p>I lavori retrò sono semplici, veloci e anche un po' divertenti. Guarda il video e leggi il
                    passaggi seguenti, quindi sei pronto per registrare e scorrere la cronologia degli acquisti del Play Store.</p>
                <div class="text-with-icon">
                <div class="icon">⚠️</div>
                <divclass="text">
                    Pagheremo i premi MSR solo per i video che soddisfano pienamente i passaggi seguenti.
                </div>
                </div>`,
                steps: [
                    {
                    step_type: 'numbered_step',
                    number: '1',
                    description_html: "<p>Prima di avviare la registrazione dello schermo, apri l'app <b>Google Play Store</b>.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '2',
                    description_html: "<p>Tocca l'icona del menu in alto a sinistra e seleziona <b>Account</b>.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '3',
                    description_html: '<p>Nella parte superiore della pagina seleziona <b>Cronologia acquisti</b>.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '4',
                    description_html: '<p>Ora puoi avviare la registrazione dello schermo.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '5',
                    description_html: "<p>Scorri verso il basso l'elenco degli acquisti nel Play Store fino alla fine.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '6',
                    description_html: '<p>Assicurati che tutta la cronologia degli acquisti sia stata acquisita, quindi interrompi la registrazione del video.</p>'
                    },
                ],
            },
            fr: {
                summary_html: '',
                summary_no_video_html: `
                    <p>Les tâches rétro sont simples, rapides et un peu amusantes aussi. Regardez la vidéo et lisez le
                    étapes ci-dessous, puis vous êtes prêt à cliquer sur Enregistrer et à faire défiler votre historique d'achats sur le Play Store.</p>
                    <div class="text-with-icon">
                        <div class="icon">⚠️</div>
                        <div classe="text">
                            Nous ne paierons des récompenses MSR que pour les vidéos qui correspondent entièrement aux étapes ci-dessous.
                        </div>
                    </div>`,
                steps: [
                    {
                    step_type: 'numbered_step',
                    number: '1',
                    description_html: "<p>Avant de démarrer l'enregistrement d'écran, ouvrez l'application <b>Google Play Store</b>.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '2',
                    description_html: "<p>Appuyez sur l'icône de menu en haut à gauche et sélectionnez <b>Compte</b>.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '3',
                    description_html: "<p>En haut de la page, sélectionnez <b>Historique des achats</b>.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '4',
                    description_html: "<p>Vous pouvez maintenant démarrer l'enregistrement d'écran.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '5',
                    description_html: "<p>Faites défiler la liste des achats sur le Play Store jusqu'à la fin.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '6',
                    description_html: "<p>Assurez-vous que tout l'historique de vos achats est capturé, puis arrêtez l'enregistrement vidéo.</p>"
                    },
                ],
            },
            de: {
                summary_html: '',
                summary_no_video_html: `
                    <p>Retro-Jobs sind einfach, schnell und machen auch ein bisschen Spaß. Sehen Sie sich das Video an und lesen Sie die
                    Führen Sie die folgenden Schritte aus, und dann können Sie auf „Aufzeichnen“ klicken und durch Ihre Play Store-Kaufhistorie scrollen.</p>
                <div class="text-with-icon">
                <div class="icon">⚠️</div>
                <div class="text">
                    Wir zahlen MSR-Prämien nur für Videos, die die folgenden Schritte vollständig erfüllen.
                    </div>
                    </div>`,
                steps: [
                    {
                    step_type: 'numbered_step',
                    number: '1',
                    description_html: '<p>Bevor Sie mit der Bildschirmaufzeichnung beginnen, öffnen Sie die <b>Google Play Store</b>-App.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '2',
                    description_html: "<p>Tippen Sie auf das Menüsymbol oben links und wählen Sie <b>Konto</b>.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '3',
                    description_html: '<p>Wählen Sie oben auf der Seite <b>Kaufhistorie</b> aus.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '4',
                    description_html: '<p>Jetzt können Sie mit der Bildschirmaufzeichnung beginnen.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '5',
                    description_html: '<p>Scrollen Sie durch die Liste der Play Store-Käufe bis zum Ende.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '6',
                    description_html: '<p>Stellen Sie sicher, dass Ihre gesamte Kaufhistorie erfasst wird, und stoppen Sie dann die Videoaufzeichnung.</p>'
                    },
                ],                
            },
            pt: {
                summary_html: '',
                summary_no_video_html: `
                    <p>Os trabalhos retrô são simples, rápidos e divertidos também. Assista ao vídeo e leia o siga as etapas abaixo e você estará pronto para gravar e percorrer seu histórico de compras na Play Store.</p>
                    <div class="text-with-icon">
                        <div class="icon">⚠️</div>
                        <div class="texto">
                            Pagaremos recompensas MSR apenas por vídeos que correspondam totalmente às etapas abaixo.
                        </div>
                    </div>`,
                steps: [
                    {
                    step_type: 'numbered_step',
                    number: '1',
                    description_html: '<p>Antes de iniciar a gravação de tela, abra o aplicativo <b>Google Play Store</b>.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '2',
                    description_html: "<p>Toque no ícone do menu no canto superior esquerdo e selecione <b>Conta</b>.</p>"
                    },
                    {
                    step_type: 'numbered_step',
                    number: '3',
                    description_html: '<p>No topo da página selecione <b>Histórico de compras</b>.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '4',
                    description_html: '<p>Agora você pode iniciar a gravação da tela.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '5',
                    description_html: '<p>Percorra a lista de compras na Play Store até chegar ao fim.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '6',
                    description_html: '<p>Certifique-se de que todo o seu histórico de compras foi capturado e pare a gravação do vídeo.</p>'
                    },
                ],
            },
        },
    },
    'app_store_purchase_history_2024': {
        ios: {
            en: {
                video_thumbnail: 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/app-store-purchase-history-ios/ios-app-store-purchase-video-thumbnail.jpeg',
                // video_url: 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/app-store-purchase-history-ios/in-app-purchase-video.mp4',
                // video_poster: 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/app-store-purchase-history-ios/app-store-purchase-poster.jpg',
                summary_html: "<p>Retro jobs are simple, quick and a bit of fun too. Read the steps below, and then you're ready to hit record and scroll through your App Store purchase history.</p>",
                summary_no_video_html: "<p>Retro jobs are simple, quick and a bit of fun too. Read the steps below, and then you're ready to hit record and scroll through your App Store purchase history.</p>",
                steps: [
                    {
                        step_type: 'numbered_step',
                        number: '1',
                        description_html: '<p>Open the <b>App Store</b> app.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '2',
                        description_html: "<p>In the app, tap on your account (via your profile icon, top-right). <i>Do not</i> tap on Purchases, it's the wrong section for this task.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '3',
                        description_html: '<p>Instead tap on your account name (usually the first item). Scroll down and tap on <b>Purchase History</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '4',
                        description_html: `<p>Tap on <b>Showing:</b> to update the filtering.</p>`
                    },
                    {
                        step_type: 'numbered_step',
                        number: '5',
                        description_html: `<p>Change date range to <b>2024</b> and Cost to both <b>Free</b> and <b>Paid</b>.</p>`
                    },
                    {
                        step_type: 'numbered_step',
                        number: '6',
                        description_html: `<p>Tap <b>Apply</b> and begin screen recording.</p>`
                    },
                    {
                        step_type: 'numbered_step',
                        number: '7',
                        description_html: '<p>Scroll to the end before you stop recording.</p>'
                    }
                ],
            },
            es: {
                data_type_description: 'Historial de compras de la tienda de aplicaciones',
                summary_html: "<p>Los trabajos retro son simples, rápidos y también un poco divertidos. Mire el video y lea los pasos a continuación, y luego estará listo para presionar grabar y desplazarse por el historial de compras de su App Store.</p>",
                summary_no_video_html: "<p>Los trabajos retro son simples, rápidos y también un poco divertidos. Lea los pasos a continuación, y luego estará listo para presionar grabar y desplazarse por el historial de compras de su App Store.</p>",
                steps: [
                    {
                        step_type: 'numbered_step',
                        number: '1',
                        description_html: '<p>Abra la aplicación <b>App Store</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '2',
                        description_html: "<p>En la aplicación, toque su cuenta (a través del ícono de su perfil, arriba a la derecha). <i>No</i> toque Compras, es la sección incorrecta para esta tarea.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '3',
                        description_html: '<p>En su lugar, toque el nombre de su cuenta (generalmente el primer elemento). Desplázate hacia abajo y toca <b>Historial de compras</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '4',
                        description_html: `<p>Cambie el intervalo de fechas a <b>${new Date().getFullYear()}</b> y <b>Todo</b>. Comience su grabación.</p>`
                    },
                    {
                        step_type: 'numbered_step',
                        number: '5',
                        description_html: '<p>Desplácese hasta el final antes de detener la grabación.</p>'
                    }
                ],
            },
            it: {
                data_type_description: "Cronologia degli acquisti dell'App Store",
                summary_html: "<p>I lavori retrò sono semplici, veloci e anche un po' divertenti. Guarda il video e leggi i passaggi seguenti, quindi sei pronto per registrare e scorrere la cronologia degli acquisti dell'App Store.</p>",
                summary_no_video_html: "<p>I lavori retrò sono semplici, veloci e anche un po' divertenti. Leggi i passaggi seguenti, quindi sei pronto per registrare e scorrere la cronologia degli acquisti dell'App Store.</p>",
                steps: [
                    {
                        step_type: 'numbered_step',
                        number: '1',
                        description_html: "<p>Apri l'app <b>App Store</b>.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '2',
                        description_html: "<p>Nell'app, tocca il tuo account (tramite l'icona del tuo profilo, in alto a destra). <i>Non</i> toccare Acquisti, è la sezione sbagliata per questa attività.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '3',
                        description_html: '<p>Tocca invece il nome del tuo account (di solito il primo elemento). Scorri verso il basso e tocca <b>Cronologia acquisti</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '4',
                        description_html: `<p>Cambia l'intervallo di date in <b>${new Date().getFullYear()}</b> e <b>Tutti</b>. Inizia la tua registrazione.</p>`
                    },
                    {
                        step_type: 'numbered_step',
                        number: '5',
                        description_html: '<p>Scorri fino alla fine prima di interrompere la registrazione.</p>'
                    }
                ],
            },
            fr: {
                summary_html: "<p>Les tâches rétro sont simples, rapides et un peu amusantes aussi. Regardez la vidéo et lisez les étapes ci-dessous, puis vous êtes prêt à cliquer sur l'enregistrement et à faire défiler l'historique de vos achats sur l'App Store.</p>",
                summary_no_video_html: "<p>Les tâches rétro sont simples, rapides et un peu amusantes aussi. Lisez les étapes ci-dessous et vous serez ensuite prêt à cliquer sur Enregistrer et à faire défiler votre historique d'achats sur l'App Store.</p>",
                steps: [
                    {
                        step_type: 'numbered_step',
                        number: '1',
                        description_html: "<p>Ouvrez l'application <b>App Store</b>.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '2',
                        description_html: "<p>Dans l'application, appuyez sur votre compte (via l'icône de votre profil, en haut à droite). <i>Ne pas</i> appuyer sur Achats, ce n'est pas la bonne section pour cette tâche.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '3',
                        description_html: '<p>Appuyez plutôt sur le nom de votre compte (généralement le premier élément). Faites défiler vers le bas et appuyez sur <b>Historique des achats</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '4',
                        description_html: `<p>Modifiez la plage de dates en <b>${new Date().getFullYear()}></b> et <b>Tous</b>. Démarrez votre enregistrement.</p>`
                    },
                    {
                        step_type: 'numbered_step',
                        number: '5',
                        description_html: "<p>Faites défiler jusqu'à la fin avant d'arrêter l'enregistrement.</p>"
                    }
                ],
            },
            de: {
                summary_html: "<p>Retro-Jobs sind einfach, schnell und machen auch ein bisschen Spaß. Sehen Sie sich das Video an und lesen Sie die Schritte unten, dann können Sie auf „Aufzeichnen“ klicken und durch Ihre Kaufhistorie im App Store scrollen.</p>",
                summary_no_video_html: "<p>Retro-Jobs sind einfach, schnell und machen auch ein bisschen Spaß. Lesen Sie die folgenden Schritte, dann können Sie auf „Aufzeichnen“ klicken und durch Ihre Kaufhistorie im App Store scrollen.</p>",
                steps: [
                    {
                        step_type: 'numbered_step',
                        number: '1',
                        description_html: '<p>Öffnen Sie die <b>App Store</b> App.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '2',
                        description_html: "<p>Tippen Sie in der App auf Ihr Konto (über Ihr Profilsymbol oben rechts). <i>Tippen Sie nicht</i> auf „Käufe“, das ist der falsche Abschnitt für diese Aufgabe.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '3',
                        description_html: '<p>Tippen Sie stattdessen auf Ihren Kontonamen (normalerweise das erste Element). Scrollen Sie nach unten und tippen Sie auf <b>Kaufhistorie</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '4',
                        description_html: `<p>Ändern Sie den Datumsbereich in <b>${new Date().getFullYear()}</b> und <b>Alle</b>.“ Starten Sie Ihre Aufnahme.</p>`
                    },
                    {
                        step_type: 'numbered_step',
                        number: '5',
                        description_html: '<p>Scrollen Sie bis zum Ende, bevor Sie die Aufnahme beenden.</p>'
                    }
                ],
            },
            pt: {
                summary_html: "<p>Trabalhos retrô são simples, rápidos e divertidos também. Assista ao vídeo e leia as etapas abaixo, e então você estará pronto para gravar e percorrer seu histórico de compras na App Store.</p>",
                summary_no_video_html: "<p>Trabalhos retrô são simples, rápidos e divertidos também. Leia as etapas abaixo e você estará pronto para gravar e percorrer seu histórico de compras na App Store.</p>",
                steps: [
                    {
                        step_type: 'numbered_step',
                        number: '1',
                        description_html: '<p>Abra o aplicativo <b>App Store</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '2',
                        description_html: "<p>No aplicativo, toque na sua conta (através do ícone do seu perfil, canto superior direito). <i>Não</i> toque em Compras, é a seção errada para esta tarefa.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '3',
                        description_html: '<p>Em vez disso, toque no nome da sua conta (geralmente o primeiro item). Role para baixo e toque em <b>Histórico de compras</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '4',
                        description_html: `<p>Altere o intervalo de datas para <b>${new Date().getFullYear()}</b> e <b>All</b>. Comece sua gravação.</p>`
                    },
                    {
                        step_type: 'numbered_step',
                        number: '5',
                        description_html: '<p>Role até o final antes de parar a gravação.</p>'
                    }
                ],
            },
        },
        android: {
            en: {
                summary_html: '',
                summary_no_video_html: `
                    <p>Retro jobs are simple, quick and a bit of fun too. Watch the video and read the
                        steps below, and then you're ready to hit record and scroll through your Play Store Purchase History.</p>
                    <div class="text-with-icon">
                    <div class="icon">⚠️</div>
                    <div class="text">
                        We’ll only pay MSR rewards for videos that fully match the steps below.
                    </div>
                    </div>`,
                steps: [
                    {
                        step_type: 'numbered_step',
                        number: '1',
                        description_html: '<p>Start recording and open the <b>Google Play Store</b> app.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '2',
                        description_html: "<p>Tap on the <b>Profile Icon</b> in the top right corner of the screen.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '3',
                        description_html: '<p>Select <b>Payments and subscriptions</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '4',
                        description_html: '<p>Click on <b>Budget and history</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '5',
                        description_html: '<p>Scroll to the bottom of the page as fast as you can (allow the page to load if there are more items).</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '6',
                        description_html: '<p>Stop screen recording when done.</p>'
                    },
                ],
            },
            es: {
                data_type_description: 'Historial de compras de la tienda de aplicaciones',
                summary_html: '',
                summary_no_video_html: `
                    <p>Los trabajos retro son simples, rápidos y también un poco divertidos. Mira el video y lee el
                    pasos a continuación, y luego estará listo para presionar grabar y desplazarse por su Historial de compras de Play Store.</p>
                <div class="text-with-icon">
                <div class="icon">⚠️</div>
                <div clase="text">
                    Solo pagaremos recompensas de MSR por videos que coincidan completamente con los pasos a continuación.
                </div>
                </div>`,
                steps: [
                    {
                        step_type: 'numbered_step',
                        number: '1',
                        description_html: '<p>Antes de comenzar a grabar la pantalla, abra la aplicación <b>Google Play Store</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '2',
                        description_html: "<p>Toca el ícono de menú en la esquina superior izquierda y selecciona <b>Cuenta</b>.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '3',
                        description_html: '<p>En la parte superior de la página, seleccione <b>Historial de compras</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '4',
                        description_html: '<p>Ahora puede iniciar la grabación de pantalla.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '5',
                        description_html: '<p>Desplácese hacia abajo por la lista de compras de Play Store hasta llegar al final.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '6',
                        description_html: '<p>Asegúrese de capturar todo su historial de compras, luego detenga la grabación de video.</p>'
                    },
                ],
            },
            it: {
                data_type_description: "Cronologia degli acquisti dell'App Store",
                summary_html: '',
                summary_no_video_html: `
                    <p>I lavori retrò sono semplici, veloci e anche un po' divertenti. Guarda il video e leggi il
                    passaggi seguenti, quindi sei pronto per registrare e scorrere la cronologia degli acquisti del Play Store.</p>
                <div class="text-with-icon">
                <div class="icon">⚠️</div>
                <divclass="text">
                    Pagheremo i premi MSR solo per i video che soddisfano pienamente i passaggi seguenti.
                </div>
                </div>`,
                steps: [
                    {
                        step_type: 'numbered_step',
                        number: '1',
                        description_html: "<p>Prima di avviare la registrazione dello schermo, apri l'app <b>Google Play Store</b>.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '2',
                        description_html: "<p>Tocca l'icona del menu in alto a sinistra e seleziona <b>Account</b>.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '3',
                        description_html: '<p>Nella parte superiore della pagina seleziona <b>Cronologia acquisti</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '4',
                        description_html: '<p>Ora puoi avviare la registrazione dello schermo.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '5',
                        description_html: "<p>Scorri verso il basso l'elenco degli acquisti nel Play Store fino alla fine.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '6',
                        description_html: '<p>Assicurati che tutta la cronologia degli acquisti sia stata acquisita, quindi interrompi la registrazione del video.</p>'
                    },
                ],
            },
            fr: {
                summary_html: '',
                summary_no_video_html: `
                    <p>Les tâches rétro sont simples, rapides et un peu amusantes aussi. Regardez la vidéo et lisez le
                    étapes ci-dessous, puis vous êtes prêt à cliquer sur Enregistrer et à faire défiler votre historique d'achats sur le Play Store.</p>
                    <div class="text-with-icon">
                        <div class="icon">⚠️</div>
                        <div classe="text">
                            Nous ne paierons des récompenses MSR que pour les vidéos qui correspondent entièrement aux étapes ci-dessous.
                        </div>
                    </div>`,
                steps: [
                    {
                        step_type: 'numbered_step',
                        number: '1',
                        description_html: "<p>Avant de démarrer l'enregistrement d'écran, ouvrez l'application <b>Google Play Store</b>.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '2',
                        description_html: "<p>Appuyez sur l'icône de menu en haut à gauche et sélectionnez <b>Compte</b>.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '3',
                        description_html: "<p>En haut de la page, sélectionnez <b>Historique des achats</b>.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '4',
                        description_html: "<p>Vous pouvez maintenant démarrer l'enregistrement d'écran.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '5',
                        description_html: "<p>Faites défiler la liste des achats sur le Play Store jusqu'à la fin.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '6',
                        description_html: "<p>Assurez-vous que tout l'historique de vos achats est capturé, puis arrêtez l'enregistrement vidéo.</p>"
                    },
                ],
            },
            de: {
                summary_html: '',
                summary_no_video_html: `
                    <p>Retro-Jobs sind einfach, schnell und machen auch ein bisschen Spaß. Sehen Sie sich das Video an und lesen Sie die
                    Führen Sie die folgenden Schritte aus, und dann können Sie auf „Aufzeichnen“ klicken und durch Ihre Play Store-Kaufhistorie scrollen.</p>
                <div class="text-with-icon">
                <div class="icon">⚠️</div>
                <div class="text">
                    Wir zahlen MSR-Prämien nur für Videos, die die folgenden Schritte vollständig erfüllen.
                    </div>
                    </div>`,
                steps: [
                    {
                        step_type: 'numbered_step',
                        number: '1',
                        description_html: '<p>Bevor Sie mit der Bildschirmaufzeichnung beginnen, öffnen Sie die <b>Google Play Store</b>-App.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '2',
                        description_html: "<p>Tippen Sie auf das Menüsymbol oben links und wählen Sie <b>Konto</b>.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '3',
                        description_html: '<p>Wählen Sie oben auf der Seite <b>Kaufhistorie</b> aus.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '4',
                        description_html: '<p>Jetzt können Sie mit der Bildschirmaufzeichnung beginnen.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '5',
                        description_html: '<p>Scrollen Sie durch die Liste der Play Store-Käufe bis zum Ende.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '6',
                        description_html: '<p>Stellen Sie sicher, dass Ihre gesamte Kaufhistorie erfasst wird, und stoppen Sie dann die Videoaufzeichnung.</p>'
                    },
                ],
            },
            pt: {
                summary_html: '',
                summary_no_video_html: `
                    <p>Os trabalhos retrô são simples, rápidos e divertidos também. Assista ao vídeo e leia o siga as etapas abaixo e você estará pronto para gravar e percorrer seu histórico de compras na Play Store.</p>
                    <div class="text-with-icon">
                        <div class="icon">⚠️</div>
                        <div class="texto">
                            Pagaremos recompensas MSR apenas por vídeos que correspondam totalmente às etapas abaixo.
                        </div>
                    </div>`,
                steps: [
                    {
                        step_type: 'numbered_step',
                        number: '1',
                        description_html: '<p>Antes de iniciar a gravação de tela, abra o aplicativo <b>Google Play Store</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '2',
                        description_html: "<p>Toque no ícone do menu no canto superior esquerdo e selecione <b>Conta</b>.</p>"
                    },
                    {
                        step_type: 'numbered_step',
                        number: '3',
                        description_html: '<p>No topo da página selecione <b>Histórico de compras</b>.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '4',
                        description_html: '<p>Agora você pode iniciar a gravação da tela.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '5',
                        description_html: '<p>Percorra a lista de compras na Play Store até chegar ao fim.</p>'
                    },
                    {
                        step_type: 'numbered_step',
                        number: '6',
                        description_html: '<p>Certifique-se de que todo o seu histórico de compras foi capturado e pare a gravação do vídeo.</p>'
                    },
                ],
            },
        },
    },
    installed_apps: {
        ios: {
            en: {
                summary_html: '',
        summary_no_video_html: `
          <p>Retro jobs are simple, quick and a bit of fun too. Read the steps below, and then you're ready to hit
              record and scroll through your installed apps.</p>
          <div class="text-with-icon">
            <div class="icon">⚠️</div>
            <div class="text">
              We’ll only pay MSR rewards for videos that fully match the steps below.
            </div>
          </div>`,
        steps: [
          {
              step_type: 'numbered_step',
              number: '1',
              description_html: '<p>Start screen recording, and then open the <b>App Store</b> app.</p>'
          },
          {
              step_type: 'numbered_step',
              number: '2',
              description_html: "<p>In the app, tap on your <b>account icon</b> in the top-right.</p>"
          },
          {
              step_type: 'numbered_step',
              number: '3',
              description_html: `<p>Tap on <b>Apps</b>.</p>`
          },
          {
              step_type: 'numbered_step',
              number: '4',
              description_html: '<p>Make sure that <b>All</b> is selected at the top of the screen, and then scroll to the bottom.</p>'
          },
          {
              step_type: 'numbered_step',
              number: '5',
              description_html: '<p>Make sure to <b style="color:red;">scroll all the way to the bottom</b>.</p>'
          },
          {
              step_type: 'numbered_step',
              number: '6',
              description_html: '<p>Now select <b>Not on this iPhone</b> at the top of the screen, and then scroll all the way back up.</p>'
          },
          {
              step_type: 'numbered_step',
              number: '7',
              description_html: '<p>Stop screen recording.</p>'
          }
        ],
            }
        },
        android: {
            en: {
                summary_html: '',
                summary_no_video_html: `
                    <p>Retro jobs are simple, quick and a bit of fun too. Read the steps below, and then you're
                        ready to hit record and scroll through your installed apps.</p>
                    <div class="text-with-icon">
                    <div class="icon">⚠️</div>
                    <div class="text">
                        We’ll only pay MSR rewards for videos that fully match the steps below.
                    </div>
                    </div>`,
                steps: [
                    {
                    step_type: 'numbered_step',
                    number: '1',
                    description_html: '<p>Start screen recording. Open the <b>Google Play Store</b> app.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '2',
                    description_html: `
                        <p>In the app, tap on your <b>account icon</b> in the top-right of the search bar.</p>
                        <p><i>This icon could be a letter or a photo.</i></p>`
                    },
                    {
                    step_type: 'numbered_step',
                    number: '3',
                    description_html: '<p>Tap <b>Manage apps & device</b> then tap on <b>Manage</b> tab.</p>'
                    },
                    {
                    step_type: 'numbered_step',
                    number: '4',
                    description_html: '<p>Scroll to the bottom. Stop screen recording.</p>'
                    }
                ],
            },
        },
    },
}

// Google Takeout can split one export across several ZIP files, so its English
// upload steps differ from the shared single-file wording: steps 5 and 6 talk
// about all the ZIP files and an "Important" note asks for every file. Steps
// 1-4, the secure-upload note, the simple steps and the other languages are
// the shared copy from getGenericUploadInstructions.
const getGoogleTakeoutUploadInstructions = () => {
    const instructions = getGenericUploadInstructions(
        'google_takeout_data_upload',
        'Google Takeout Upload',
        'https://d3s00xghdjywqy.cloudfront.net/c2/retro/google-activity/logo.png',
        'Google Takeout',
        'https://help.measureprotocol.com/en/support/solutions/articles/80001093022-google-takeout-instructions'
    );
    const en = instructions.generic.en;
    const sharedSteps = en.steps.filter(step => step.step_type === 'numbered_step');
    const secureUploadNote = en.steps.find(step => step.step_type === 'emoji_step');
    en.steps = [
        ...sharedSteps.slice(0, 4),
        {
            step_type: 'numbered_step',
            number: '5',
            description_html: '<p>Download all the .ZIP files to your mobile phone or computer.</p>'
        },
        {
            step_type: 'numbered_step',
            number: '6',
            description_html: '<p>To upload your data, tap <b>Choose File</b> below. Find all the .ZIP files you just saved and upload them.</p>'
        },
        {
            step_type: 'emoji_step',
            emoji: '⚠️',
            description_html: '<p><b>Important:</b> If Google Takeout provided more than one file, make sure you upload all provided .ZIP files to receive the full payment.</p>'
        },
        secureUploadNote,
    ];
    return instructions;
};

export const genericDataTypes = {
    'app_store_purchase_history': {
        data_type: 'app_store_purchase_history',
        data_type_description: 'App Store Purchase History',
        capture_type: 'video',
        ios: {
            use_in_app_screen_recorder: false,
            logo: 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/installed-apps-ios/logo.png',
            ...content.app_store_purchase_history.ios
        },
        android: {
            logo: 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/installed-apps-android/logo.png',
            use_in_app_screen_recorder: true,
            ...content.app_store_purchase_history.android
        },
    },
    'app_store_purchase_history_2024': {
        data_type: 'app_store_purchase_history',
        data_type_description: 'App Store Purchase History',
        capture_type: 'video',
        ios: {
            use_in_app_screen_recorder: false,
            logo: 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/installed-apps-ios/logo.png',
            ...content.app_store_purchase_history_2024.ios
        },
        android: {
            logo: 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/installed-apps-android/logo.png',
            use_in_app_screen_recorder: true,
            ...content.app_store_purchase_history_2024.android
        },
    },
    'installed_apps': {
        data_type: 'installed_apps',
        data_type_description: 'Installed Apps',
        
        capture_type: 'video',
        ios: {
            logo: 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/installed-apps-ios/logo.png',
            use_in_app_screen_recorder: false,
            ...content.installed_apps.ios,
        },
        android: {
            logo: 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/installed-apps-android/logo.png',
            use_in_app_screen_recorder: true,
            ...content.installed_apps.android,
        },  
    },
    // 'tiktok_data_upload': getGenericUploadInstructions('tiktok_data_upload', 'TikTok Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/tiktok_data_export/logo.png', 'TikTok'),
    'facebook_data_upload': getGenericUploadInstructions('facebook_data_upload', 'Facebook Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/facebook/logo.png', 'Facebook', 'https://help.measureprotocol.com/en/support/solutions/articles/80001099386-facebook-data-file-retro-instructions'),
    'instagram_data_upload': getGenericUploadInstructions('instagram_data_upload', 'Instagram Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/instagram-ad-activity/logo.jpg', 'Instagram', 'https://help.measureprotocol.com/en/support/solutions/articles/80001127112-instagram-data-file-retro-instructions'),
    'youtube_data_upload': getGenericUploadInstructions('youtube_data_upload', 'YouTube Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/youtube-watch-history/logo.png', 'YouTube'),
    'discord_data_upload': getGenericUploadInstructions('discord_data_upload', 'Discord Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/discord/logo.png', 'Discord', 'https://help.measureprotocol.com/en/support/solutions/articles/80001128765-discord-data-file-retro-instructions'),
    'snap_data_upload': getGenericUploadInstructions('snap_data_upload', 'Snap Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/snap/logo.png', 'Snapchat', 'https://help.measureprotocol.com/en/support/solutions/articles/80001128763-snapchat-data-file-retro-instructions'),
    'amazon_data_export': getGenericUploadInstructions('amazon_data_export', 'Amazon Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/amazon-order-history/logo.png', 'Amazon'),
    'uber_data_export': getGenericUploadInstructions('uber_data_export', 'Uber Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/uber-ride-history/logo.jpg', 'Uber'),
    'google_takeout_data_upload': getGoogleTakeoutUploadInstructions(),
    'netflix_data_upload': getGenericUploadInstructions('netflix_data_upload', 'Netflix Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/netflix-watch-history/logo.png', 'Netflix'),
    'apple_data_upload': getGenericAppleUploadInstructions('apple_data_upload', 'Apple Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/apple_logo_2.png', 'Apple'),
    'thumbtack_data_upload': getGenericUploadInstructions('thumbtack_data_upload', 'Thumbtack Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/thumbtack.png', 'Thumbtack'),
    'yelp_data_upload': getGenericUploadInstructions('yelp_data_upload', 'Yelp Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/yelp.png', 'Yelp'),
    'doordash_data_upload': getGenericUploadInstructions('doordash_data_upload', 'DoorDash Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/doordash.png', 'DoorDash'),
    'kayak_data_upload': getGenericUploadInstructions('kayak_data_upload', 'Kayak Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/kayak.png', 'Kayak'),
    'flipkart_data_upload': getGenericUploadInstructions('flipkart_data_upload', 'Flipkart Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/flipkart.png', 'Flipkart'),
    'ubereats_data_upload': getGenericUploadInstructions('ubereats_data_upload', 'Uber Eats Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/ubereats.png', 'Uber Eats'),
    'claude_data_upload': getGenericUploadInstructions('claude_data_upload', 'Claude Data Upload', 'https://d3s00xghdjywqy.cloudfront.net/c2/retro/claude.png', 'Claude'),
}